import PropTypes from 'prop-types';
import React, { cloneElement, useEffect, useRef } from 'react';

const eventTypeMapping = {
    click: 'onClick',
    focusin: 'onFocus',
    focusout: 'onFocus',
    mousedown: 'onMouseDown',
    mouseup: 'onMouseUp',
    touchstart: 'onTouchStart',
    touchend: 'onTouchEnd',
};

/** @param {(typeof propTypes)} props */
const ClickAwayListener = (props) => {
    const { children, onClickAway, focusEvent = 'focusin', mouseEvent = 'click', touchEvent = 'touchend' } = props;
    const node = useRef(null);
    const bubbledEventTarget = useRef(null);
    const mountedRef = useRef(false);

    /**
     * Prevents the bubbled event from getting triggered immediately
     * https://github.com/facebook/react/issues/20074
     */
    useEffect(() => {
        setTimeout(() => {
            mountedRef.current = true;
        }, 0);

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const handleBubbledEvents = (type) => (event) => {
        bubbledEventTarget.current = event.target;

        const handler = children.props[type];

        if (handler) {
            handler(event);
        }
    };

    const handleChildRef = (childRef) => {
        node.current = childRef;

        const { ref } = children;

        if (typeof ref === 'function') {
            ref(childRef);
        } else if (ref) {
            ref.current = childRef;
        }
    };

    useEffect(() => {
        const nodeDocument = node.current ? node.current.ownerDocument : document;

        const handleEvents = (event) => {
            if (!mountedRef.current) return;

            if (
                (node.current && node.current.contains(event.target)) ||
                bubbledEventTarget.current === event.target ||
                !nodeDocument.contains(event.target)
            ) {
                return;
            }

            onClickAway(event);
        };

        nodeDocument.addEventListener(mouseEvent, handleEvents);
        nodeDocument.addEventListener(touchEvent, handleEvents);
        nodeDocument.addEventListener(focusEvent, handleEvents);

        return () => {
            nodeDocument.removeEventListener(mouseEvent, handleEvents);
            nodeDocument.removeEventListener(touchEvent, handleEvents);
            nodeDocument.removeEventListener(focusEvent, handleEvents);
        };
    }, [focusEvent, mouseEvent, onClickAway, touchEvent]);

    const mappedMouseEvent = eventTypeMapping[mouseEvent];
    const mappedTouchEvent = eventTypeMapping[touchEvent];
    const mappedFocusEvent = eventTypeMapping[focusEvent];

    return React.Children.only(
        cloneElement(children, {
            ref: handleChildRef,
            [mappedFocusEvent]: handleBubbledEvents(mappedFocusEvent),
            [mappedMouseEvent]: handleBubbledEvents(mappedMouseEvent),
            [mappedTouchEvent]: handleBubbledEvents(mappedTouchEvent),
        })
    );
};

const FocusEvents = ['focusin', 'focusout'];
const MouseEvents = ['click', 'mousedown', 'mouseup'];
const TouchEvents = ['touchstart', 'touchend'];

const propTypes = {
    onClickAway: PropTypes.func.isRequired,
    focusEvent: PropTypes.oneOf(FocusEvents),
    mouseEvent: PropTypes.oneOf(MouseEvents),
    touchEvent: PropTypes.oneOf(TouchEvents),
    children: PropTypes.element,
};
ClickAwayListener.propTypes = propTypes;
ClickAwayListener.displayName = 'ClickAwayListener';

export default ClickAwayListener;
